import { useState, useEffect } from "react";
import { Box, Typography, Paper, Button, Chip, IconButton, Tooltip } from "@mui/material";
import {
  Book as BookIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as StartIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { RichTextDisplay, ExportPdfButton } from "@/components/common";
import ExerciseSessionInline from "./ExerciseSessionInline";
import { downloadExerciseSectionPdf } from "@/utils/pdfExport";
import { useSelector } from "react-redux";
import { selectIsAdmin } from "@/store/slices/authSlice";

const ExerciseSection = ({
  selectedDay,
  selectedSection,
  progress,
  sectionConfig,
  exerciseId,
  onSessionStart,
  onSessionEnd,
  isSidebarCollapsed,
  onToggleSidebar,
}) => {
  const [isSessionActive, setIsSessionActive] = useState(false);
  const isAdmin = useSelector(selectIsAdmin);

  // Map frontend section names to backend API section names
  const getSectionApiName = (section) => {
    const mapping = {
      lcExercise: 'lc-exercise',
      rcExercise: 'rc-exercise',
      attendanceTest: 'attendance-test',
    };
    return mapping[section] || section;
  };

  // Reset session when selectedSection changes
  useEffect(() => {
    setIsSessionActive(false);
    if (onSessionEnd) {
      onSessionEnd();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection]);


  const isCompleted = progress?.partProgress?.[selectedSection];

  // Get section title
  const getSectionTitle = () => {
    return sectionConfig?.name || "Section";
  };

  const handleStartExercise = () => {
    setIsSessionActive(true);
    if (onSessionStart) {
      onSessionStart();
    }
  };

  const handleExitSession = () => {
    setIsSessionActive(false);
    if (onSessionEnd) {
      onSessionEnd();
    }
  };

  // If session is active, show inline session for all exercise types
  if (isSessionActive && (selectedSection === "lcExercise" || selectedSection === "rcExercise" || selectedSection === "attendanceTest")) {
    return (
      <ExerciseSessionInline
        exerciseId={exerciseId}
        dayId={selectedDay._id}
        sectionType={selectedSection}
        sectionInfo={selectedDay[selectedSection] || {}}
        dayLabel={[selectedDay.dayNumber != null ? `Day ${selectedDay.dayNumber}` : null, selectedDay.title].filter(Boolean).join(" · ")}
        onExit={handleExitSession}
      />
    );
  }

  return (
    <Box>
      {/* Section Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
          borderRadius: 2,
          p: 3,
          mb: 3,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {sectionConfig.name}:{" "}
            {selectedDay[selectedSection]?.title || getSectionTitle()}
          </Typography>
          {/* {!isSidebarCollapsed && onToggleSidebar && (
            <Tooltip title="Collapse day list">
              <IconButton
                onClick={onToggleSidebar}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: -24,
                  color: "white",
                  bgcolor: "primary.main",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                  zIndex: 10,
                }}
              >
                <ChevronRightIcon />
                <ChevronRightIcon sx={{ ml: -1.5 }} />
              </IconButton>
            </Tooltip>
          )} */}
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              icon={<BookIcon />}
              label={`DAY ${selectedDay.dayNumber}`}
              color="warning"
              sx={{ fontWeight: "bold" }}
            />
            <Chip
              label={selectedDay.title}
              variant="outlined"
              sx={{ color: "white", borderColor: "white" }}
            />
            {isCompleted && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Completed"
                color="success"
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Content */}
      {selectedDay[selectedSection]?.description && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <RichTextDisplay
            content={selectedDay[selectedSection].description}
          />
        </Paper>
      )}

      {/* Action Buttons */}
      <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
        <Button
          variant="contained"
          size="large"
          startIcon={<StartIcon />}
          onClick={handleStartExercise}
          sx={{
            background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
            color: "white",
            px: 6,
            py: 2,
            fontSize: "1.1rem",
            fontWeight: 600,
            borderRadius: 3,
            boxShadow: "0 8px 20px rgba(33, 150, 243, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #1976D2 0%, #1565C0 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 12px 24px rgba(33, 150, 243, 0.4)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Start {sectionConfig.name}
        </Button>
        {/* Only show export for LC/RC/Attendance sections */}
        {(selectedSection === 'lcExercise' || selectedSection === 'rcExercise' || selectedSection === 'attendanceTest') && (
          <ExportPdfButton
            variant="outlined"
            size="large"
            isAdmin={isAdmin}
            onExport={() => downloadExerciseSectionPdf(
              selectedDay._id,
              getSectionApiName(selectedSection),
              `${selectedDay.title}_${sectionConfig.name}`
            )}
            onExportWithAnswers={isAdmin ? () => downloadExerciseSectionPdf(
              selectedDay._id,
              getSectionApiName(selectedSection),
              `${selectedDay.title}_${sectionConfig.name}`,
              true
            ) : undefined}
            label="Export PDF"
            sx={{
              py: 2,
              px: 4,
              borderRadius: 3,
              fontSize: "1.1rem",
            }}
          />
        )}
      </Box>
    </Box>
  );
};
export default ExerciseSection;
